import { get, writable } from 'svelte/store';

import { normalizeEmail } from 'src/helpers/email_helpers';
import { omitMockLightDevicesFromRecord } from 'src/helpers/dev/mock_light_ids';
import { set_devices_data_android } from 'src/services/lists_update_android';
import { set_devices_data_ios } from 'src/services/lists_update_ios';
import type { Device } from 'src/types/Device';

import { app, os } from 'src/stores/session/native-bridge';
import { user_data, user_settings } from '../account/user';

const DEVICES_STORAGE_KEY = 'devices';
const DEVICES_PERSIST_DELAY_MS = 1000;
const DEVICES_PERSIST_MAX_WAIT_MS = 3000;

function readPersistedDevices(): { [key: string]: Device } {
	try {
		return JSON.parse(localStorage.getItem(DEVICES_STORAGE_KEY)) || {};
	} catch {
		return {};
	}
}

function create_devices() {
	const { subscribe, set, update } = writable<{ [key: string]: Device }>(readPersistedDevices());

	return {
		subscribe,
		set,
		update: (new_device: Device) =>
			update((devices) => {
				if (devices[new_device.id] === new_device) return devices;
				return { ...devices, [new_device.id]: new_device };
			}),
		massUpdate: (new_devices: { [key: string]: Device }) => {
			update((devices: any) => {
				return { ...devices, ...new_devices };
			});
		},
		remove: (id: string) =>
			update((devices) => {
				if (!(id in devices)) return devices;
				const { [id]: _removed, ...remainingDevices } = devices;
				return remainingDevices;
			}),
		reset: () => set({}),
	};
}

export const devices = create_devices();

let pendingDevices: { [key: string]: Device } | null = null;
let persistTimer: ReturnType<typeof setTimeout> | undefined;
let persistMaxWaitTimer: ReturnType<typeof setTimeout> | undefined;
let nativeSyncTimer: ReturnType<typeof setTimeout> | undefined;
let isInitialDeviceValue = true;

const flushDevicePersistence = () => {
	if (persistTimer) clearTimeout(persistTimer);
	if (persistMaxWaitTimer) clearTimeout(persistMaxWaitTimer);
	persistTimer = undefined;
	persistMaxWaitTimer = undefined;

	if (!pendingDevices) return;

	try {
		localStorage.setItem(
			DEVICES_STORAGE_KEY,
			JSON.stringify(omitMockLightDevicesFromRecord(pendingDevices)),
		);
	} catch (error) {
		console.warn('Unable to persist the device list.', error);
	}
	pendingDevices = null;
};

const scheduleDevicePersistence = (value: { [key: string]: Device }) => {
	pendingDevices = value;
	if (persistTimer) clearTimeout(persistTimer);
	persistTimer = setTimeout(flushDevicePersistence, DEVICES_PERSIST_DELAY_MS);
	persistMaxWaitTimer ??= setTimeout(flushDevicePersistence, DEVICES_PERSIST_MAX_WAIT_MS);
};

const scheduleNativeDeviceSync = (value: { [key: string]: Device }) => {
	if (nativeSyncTimer) clearTimeout(nativeSyncTimer);
	nativeSyncTimer = setTimeout(() => {
		nativeSyncTimer = undefined;
		const _user_data = get(user_data);
		const _user_data_to_pass = {
			password: _user_data.password,
			username: normalizeEmail(_user_data.email),
			user_url: _user_data.user_api_url,
			user_id: _user_data.user_id,
			lang: get(user_settings).lang,
		};

		if (_user_data.password !== '') {
			const persistableDevices = omitMockLightDevicesFromRecord(value);
			if (get(os) !== 'IOS') {
				set_devices_data_android(_user_data_to_pass, persistableDevices);
			} else {
				set_devices_data_ios(_user_data_to_pass, persistableDevices);
			}
		}
	}, DEVICES_PERSIST_MAX_WAIT_MS);
};

devices.subscribe((val: { [key: string]: Device }) => {
	if (isInitialDeviceValue) {
		isInitialDeviceValue = false;
		return;
	}

	scheduleDevicePersistence(val);

	if (get(app) && get(user_data).email) {
		scheduleNativeDeviceSync(val);
	}
});

if (typeof window !== 'undefined') {
	window.addEventListener('pagehide', flushDevicePersistence);
}
